import { useProjectCreationStore } from "@/store/project-creation-store";

/**
 * Utility function to clear all project creation store data
 * This function resets both project type and project scope data to their default values
 * and clears the data from localStorage
 */
export const clearProjectCreationStore = () => {
  const store = useProjectCreationStore.getState();
  store.clearAllData();
};

/**
 * Utility function to clear only project type data
 * Useful when you want to reset only the project type step
 */
export const clearProjectTypeData = () => {
  const store = useProjectCreationStore.getState();
  store.clearProjectTypeData();
};

/**
 * Utility function to clear only project scope data
 * Useful when you want to reset only the project scope step
 */
export const clearProjectScopeData = () => {
  const store = useProjectCreationStore.getState();
  store.clearProjectScopeData();
};

/**
 * Utility function to check if the store has any data
 * Returns true if either project type or project scope has data
 */
export const hasProjectCreationData = (): boolean => {
  const store = useProjectCreationStore.getState();
  const { projectType, projectScope } = store;

  const hasProjectTypeData = Boolean(
    projectType.structuralType ||
      projectType.placeDescription ||
      projectType.projectId ||
      projectType.projectTypeId
  );

  const hasProjectScopeData = Boolean(
    projectScope.scopeOfWork ||
      projectScope.projectScopeId ||
      projectScope.projectSection
  );

  return hasProjectTypeData || hasProjectScopeData;
};

/**
 * Utility function to get current store state for debugging
 * Returns the current state of both project type and project scope
 */
export const getProjectCreationStoreState = () => {
  const store = useProjectCreationStore.getState();
  return {
    projectType: store.projectType,
    projectScope: store.projectScope,
    isProjectTypeComplete: store.isProjectTypeComplete(),
    isProjectScopeComplete: store.isProjectScopeComplete(),
  };
};

// Example usage scenarios:
/*
// 1. Clear all data when user logs out or visits certain pages
import { clearProjectCreationStore } from '@/lib/store-utils/store.util';

// In a logout function or page component
const handleLogout = () => {
  clearProjectCreationStore();
  // ... other logout logic
};

// 2. Clear only project scope data when user goes back to project type
import { clearProjectScopeData } from '@/lib/store-utils/store.util';

const handleGoBackToProjectType = () => {
  clearProjectScopeData();
  // Navigate to project type step
};

// 3. Check if there's any data before showing a warning
import { hasProjectCreationData } from '@/lib/store-utils/store.util';

const handlePageNavigation = () => {
  if (hasProjectCreationData()) {
    const shouldClear = confirm('You have unsaved project data. Clear it?');
    if (shouldClear) {
      clearProjectCreationStore();
    }
  }
};

// 4. Debug current state
import { getProjectCreationStoreState } from '@/lib/store-utils/store.util';

const debugStore = () => {
  const state = getProjectCreationStoreState();
  console.log('Current store state:', state);
};
*/
